import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/shared/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { Input } from "@/shared/components/ui/input";
import { Textarea } from "@/shared/components/ui/textarea";
import { Label } from "@/shared/components/ui/label";
import { useToast } from "@/shared/hooks/use-toast";
import { apiClient } from "@/shared/services/api";
import { trackViewContent, trackLead } from "@/shared/utils/metaPixel";
import {
  Mail,
  Phone,
  MapPin,
  Clock,
  ArrowRight,
  ArrowLeft,
  Send
} from "lucide-react";

export default function ContactUsPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    subject: '',
    message: ''
  });

  // Track ViewContent for Meta Pixel on page load
  useEffect(() => {
    trackViewContent({
      contentName: 'Contact Us Page',
      contentCategory: 'Contact',
    });
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // SECURITY: Use backend proxy instead of calling edge function directly
      await apiClient.post('/edge/send-contact-email', formData);

      // Track Lead event for Meta Pixel (contact form submission = lead)
      const nameParts = formData.name.split(' ');
      trackLead({
        email: formData.email,
        firstName: nameParts[0] || '',
        lastName: nameParts.slice(1).join(' ') || '',
      });

      toast({
        title: "Message sent!",
        description: "We'll get back to you within 24 hours.",
      });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        company: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('Error sending contact form:', error);
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGetStarted = () => {
    navigate('/auth?mode=signup', { state: { from: '/contact' } });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-background/95 backdrop-blur-sm border-b border-border z-50">
        <div className="container mx-auto px-6 py-2">
          <div className="flex items-center justify-between">
            {/* Logo and left navigation */}
            <div className="flex items-center -ml-4 md:ml-0">
              <button 
                onClick={() => {
                  navigate('/');
                  setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
                }}
                className="text-xs md:text-2xl font-bold text-primary hover:text-primary/90 transition-colors"
              >
                wlkthru
              </button>
              
              {/* Left navigation - next to logo */}
              <nav className="hidden md:flex space-x-8 ml-8">
                <button
                  onClick={() => navigate('/features')}
                  className="text-sm font-medium transition-colors hover:text-primary text-foreground"
                >
                  Features
                </button>
                <button
                  onClick={() => navigate('/pricing')}
                  className="text-sm font-medium transition-colors hover:text-primary text-foreground"
                >
                  Pricing
                </button>
              </nav>
              
              {/* Mobile navigation - next to logo */}
              <nav className="md:hidden flex space-x-3 ml-3">
                <button
                  onClick={() => navigate('/features')}
                  className="text-xs font-medium transition-colors hover:text-primary text-foreground"
                >
                  Features
                </button>
                <button
                  onClick={() => navigate('/pricing')}
                  className="text-xs font-medium transition-colors hover:text-primary text-foreground"
                >
                  Pricing
                </button>
              </nav>
            </div>

            {/* Mobile Navigation */}
            <div className="md:hidden flex items-center">
              <button 
                onClick={() => navigate('/auth')}
                className="text-xs text-muted-foreground px-2 py-1.5 hover:text-foreground transition-colors cursor-pointer"
              >
                Log in
              </button>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-4">
              <button 
                onClick={() => navigate('/auth')}
                className="text-sm text-muted-foreground px-3 py-2 hover:text-foreground transition-colors cursor-pointer"
              >
                Log in
              </button>
              <button 
                onClick={handleGetStarted}
                className="text-sm px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90 rounded-md transition-colors cursor-pointer"
              >
                Get started for free
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Back Button */}
      <div className="pt-20 pb-4 bg-background">
        <div className="container mx-auto px-6">
          <button
            onClick={() => {
              navigate('/');
              setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
            }}
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
        </div>
      </div>

      {/* Hero Section */}
      <section className="pb-8 bg-background">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
             <h1 className="text-5xl md:text-6xl font-bold text-foreground leading-tight mb-8">
               Get <span className="text-primary">in</span>{" "}
               <span className="text-primary">touch</span>
             </h1>
            <p className="text-xl text-muted-foreground leading-relaxed mb-12 max-w-3xl mx-auto">
              Have questions about wlkthru? Want to see a demo? We'd love to hear from you.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="pb-20 bg-background">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            {/* Contact Form */}
            <div>
              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl">Send us a message</CardTitle>
                  <CardDescription>
                    Fill out the form below and we'll get back to you as soon as possible.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          placeholder="Your full name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          placeholder="your@email.com"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="company">Company</Label>
                      <Input
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        placeholder="Your company name"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="subject">Subject *</Label>
                      <Input
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        required
                        placeholder="What's this about?"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        placeholder="Tell us more about your needs..."
                        className="min-h-[120px]"
                      />
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full" 
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        "Sending..."
                      ) : (
                        <>
                          Send Message
                          <Send className="ml-2 h-4 w-4" />
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 pb-6 bg-primary text-center md:text-left">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-white">
            {/* Brand Section */}
            <div className="space-y-4">
              <button 
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="text-4xl font-bold hover:text-white/80 transition-colors cursor-pointer"
              >
                wlkthru
              </button>
              <div className="w-16 h-0.5 bg-white/30 mx-auto md:mx-0"></div>
              <div className="space-y-3">
                <p className="text-white/90">Check us out on:</p>
                <div className="flex gap-2 justify-center">
                  <a 
                    href="https://www.facebook.com/profile.php?id=61578600642587" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
                  >
                    <span className="text-xs text-white">f</span>
                  </a>
                  <a 
                    href="https://www.instagram.com/wlkthru.co/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
                  >
                    <span className="text-xs text-white">ig</span>
                  </a>
                  <a 
                    href="https://www.linkedin.com/company/wlkthruco/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
                  >
                    <span className="text-xs text-white">in</span>
                  </a>
                  <a 
                    href="https://www.youtube.com/@wlkthruco" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
                  >
                    <span className="text-xs text-white">yt</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Get in Touch and Legal */}
            <div className="flex flex-col md:flex-row gap-8 md:col-span-2 justify-end">
              {/* Get in Touch */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-white">Get in Touch</h4>
                <ul className="space-y-2">
                  <li><a href="#" className="text-white/80 hover:text-white transition-colors">• Customer Support</a></li>
                  <li><a href="#" className="text-white/80 hover:text-white transition-colors">• Demo Request</a></li>
                </ul>
              </div>

              {/* Legal */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-white">Legal</h4>
                <ul className="space-y-2">
                  <li><button onClick={() => { navigate('/legal?section=privacy'); setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100); }} className="text-white/80 hover:text-white transition-colors text-left">• Privacy Policy</button></li>
                  <li><button onClick={() => { navigate('/legal?section=terms'); setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100); }} className="text-white/80 hover:text-white transition-colors text-left">• Terms and Conditions</button></li>
                  <li><button onClick={() => { navigate('/legal?section=disclaimer'); setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100); }} className="text-white/80 hover:text-white transition-colors text-left">• Disclaimer</button></li>
                </ul>
              </div>
            </div>
          </div>

          {/* Copyright */}
          <div className="mt-4 pt-2 border-t border-white/20 text-center">
            <p className="text-white/60 text-xs font-light">
              Copyright © 2025 wlkthru | All rights reserved
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}